import React from 'react';
import { EmpModal as Modal, EmpButton as Button } from '@empowernow/ui';
import {
  startActivityTracking,
  onCrossTabActivity,
  getLastActivity,
  recordActivity,
} from './ActivitySync';

// ============================================================================
// Types
// ============================================================================

type ActionState = 'idle' | 'loading' | 'success';
type UrgencyLevel = 'normal' | 'warning' | 'critical';

type Props = {
  idleMs?: number;        // time to show prompt
  timeoutMs?: number;     // time after prompt to auto-logout
};

// ============================================================================
// Helpers
// ============================================================================

function getUrgencyLevel(seconds: number): UrgencyLevel {
  if (seconds <= 10) return 'critical';
  if (seconds <= 30) return 'warning';
  return 'normal';
}

function ensureKeyframes(): void {
  const styleId = 'idle-prompt-keyframes';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes idlePromptFadeIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes idlePromptSlideUp {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes idlePromptShake {
      0%, 100% { transform: translateX(0); }
      20% { transform: translateX(-4px); }
      40% { transform: translateX(4px); }
      60% { transform: translateX(-2px); }
      80% { transform: translateX(2px); }
    }
    @keyframes idlePromptSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes idlePromptPulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
    @keyframes idlePromptCheckmark {
      0% { stroke-dashoffset: 24; }
      100% { stroke-dashoffset: 0; }
    }
  `;
  document.head.appendChild(style);
}

// ============================================================================
// Sub-components
// ============================================================================

function Spinner(): React.ReactElement {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 16,
        height: 16,
        border: '2px solid rgba(255,255,255,0.3)',
        borderTopColor: 'currentColor',
        borderRadius: '50%',
        animation: 'idlePromptSpin 0.8s linear infinite',
      }}
      aria-hidden="true"
    />
  );
}

function Checkmark(): React.ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'inline-block' }}
      aria-hidden="true"
    >
      <polyline
        points="20 6 9 17 4 12"
        style={{
          strokeDasharray: 24,
          strokeDashoffset: 0,
          animation: 'idlePromptCheckmark 0.3s ease-out',
        }}
      />
    </svg>
  );
}

type CountdownRingProps = {
  remaining: number;
  total: number;
  urgency: UrgencyLevel;
};

function CountdownRing({ remaining, total, urgency }: CountdownRingProps): React.ReactElement {
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const progress = remaining / total;
  const offset = circumference * (1 - progress);

  const ringColor = urgency === 'critical'
    ? 'var(--error-color, #ef4444)'
    : urgency === 'warning'
      ? 'var(--warning-color, #f59e0b)'
      : 'var(--main-accent, #00E7F6)';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--spacing-xs, 8px)',
        animation: 'idlePromptSlideUp 0.4s ease-out',
      }}
    >
      <svg
        width="120"
        height="120"
        viewBox="0 0 100 100"
        style={{ transform: 'rotate(-90deg)' }}
        role="progressbar"
        aria-valuenow={remaining}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={`${remaining} seconds remaining`}
      >
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="var(--border-color, rgba(255,255,255,0.1))"
          strokeWidth="6"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset 1s linear, stroke 0.3s ease',
            filter: urgency === 'critical' ? 'drop-shadow(0 0 6px currentColor)' : undefined,
          }}
        />
      </svg>
      <span
        style={{
          position: 'absolute',
          fontSize: 'var(--font-size-2xl, 28px)',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: ringColor,
          marginTop: 46,
          animation: urgency === 'critical' ? 'idlePromptPulse 0.5s ease-in-out infinite' : undefined,
        }}
      >
        {remaining}
      </span>
    </div>
  );
}

export function IdlePrompt({ idleMs = 14 * 60_000, timeoutMs = 60_000 }: Props) {
  const [visible, setVisible] = React.useState(false);
  const [remaining, setRemaining] = React.useState<number>(Math.floor(timeoutMs / 1000));
  const [actionState, setActionState] = React.useState<ActionState>('idle');
  const [announced, setAnnounced] = React.useState<Set<number>>(new Set());
  
  const deadlineRef = React.useRef<number | null>(null);
  const idleTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const totalSeconds = Math.floor(timeoutMs / 1000);

  // Inject keyframes on mount
  React.useEffect(() => {
    ensureKeyframes();
  }, []);

  const urgency = getUrgencyLevel(remaining);

  const scheduleIdlePrompt = React.useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    const lastActivity = getLastActivity();
    const elapsed = Date.now() - lastActivity;
    const timeUntilIdle = Math.max(0, idleMs - elapsed);

    idleTimerRef.current = setTimeout(() => {
      const recentActivity = getLastActivity();
      const recentElapsed = Date.now() - recentActivity;
      
      if (recentElapsed >= idleMs) {
        setVisible(true);
        setRemaining(Math.floor(timeoutMs / 1000));
        setActionState('idle');
        setAnnounced(new Set());
        deadlineRef.current = Date.now() + timeoutMs;
      } else {
        scheduleIdlePrompt();
      }
    }, timeUntilIdle);
  }, [idleMs, timeoutMs]);

  const resetActivity = React.useCallback(() => {
    deadlineRef.current = null;
    setVisible(false);
    setActionState('idle');
    recordActivity();
    scheduleIdlePrompt();
  }, [scheduleIdlePrompt]);

  // Keyboard shortcuts
  React.useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && actionState === 'idle') {
        e.preventDefault();
        handleStaySignedIn();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, actionState]);

  React.useEffect(() => {
    const stopLocalTracking = startActivityTracking();

    const stopCrossTabListening = onCrossTabActivity(() => {
      if (visible) {
        deadlineRef.current = null;
        setVisible(false);
        setActionState('idle');
      }
      scheduleIdlePrompt();
    });

    scheduleIdlePrompt();

    const tickTimer = setInterval(() => {
      if (visible && deadlineRef.current) {
        const secs = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
        setRemaining(secs);
        if (Date.now() >= deadlineRef.current) {
          const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.replace(`/auth/logout?return_to=${returnTo}`);
        }
      }
    }, 1000);

    const fallbackCheck = setInterval(() => {
      if (!visible) {
        const lastActivity = getLastActivity();
        const elapsed = Date.now() - lastActivity;
        if (elapsed < idleMs) {
          scheduleIdlePrompt();
        }
      }
    }, 5000);

    return () => {
      stopLocalTracking();
      stopCrossTabListening();
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      clearInterval(tickTimer);
      clearInterval(fallbackCheck);
    };
  }, [idleMs, timeoutMs, visible, scheduleIdlePrompt]);

  const handleStaySignedIn = async () => {
    if (actionState !== 'idle') return;
    
    setActionState('loading');
    try { 
      await fetch('/auth/session', { credentials: 'include', cache: 'no-store' }); 
      setActionState('success');
      // Brief success state before dismissing
      setTimeout(() => {
        resetActivity();
      }, 600);
    } catch {
      // Still dismiss on error - session might still be valid
      resetActivity();
    }
  };

  const handleSignOut = () => {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.replace(`/auth/logout?return_to=${returnTo}`);
  };

  // Screen reader announcements at key thresholds
  const announcement = React.useMemo(() => {
    const thresholds = [30, 10, 5];
    for (const t of thresholds) {
      if (remaining === t && !announced.has(t)) {
        setAnnounced(prev => new Set(prev).add(t));
        return `${remaining} seconds until automatic sign out`;
      }
    }
    return null;
  }, [remaining, announced]);

  // Urgency-based styling
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-lg, 20px)',
    padding: 'var(--spacing-md, 16px)',
    animation: visible ? 'idlePromptFadeIn 0.3s ease-out' : undefined,
  };

  const bannerStyle: React.CSSProperties = {
    padding: 'var(--spacing-md, 16px)',
    borderRadius: 'var(--border-radius-md, 8px)',
    textAlign: 'center' as const,
    transition: 'all 0.3s ease',
    animation: urgency === 'critical' ? 'idlePromptShake 0.5s ease-out' : undefined,
    background: urgency === 'critical'
      ? 'var(--error-bg, rgba(239, 68, 68, 0.12))'
      : urgency === 'warning'
        ? 'var(--warning-bg, rgba(245, 158, 11, 0.12))'
        : 'var(--info-bg, rgba(59, 130, 246, 0.08))',
    border: urgency === 'critical'
      ? '2px solid var(--error-border, rgba(239, 68, 68, 0.4))'
      : urgency === 'warning'
        ? '2px solid var(--warning-border, rgba(245, 158, 11, 0.4))'
        : '1px solid var(--info-border, rgba(59, 130, 246, 0.2))',
  };

  const messageStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-md, 15px)',
    color: urgency === 'critical'
      ? 'var(--error-text, #fca5a5)'
      : urgency === 'warning'
        ? 'var(--warning-text, #fcd34d)'
        : 'var(--main-text, inherit)',
    fontWeight: urgency === 'critical' ? 600 : 400,
    margin: 0,
    animation: 'idlePromptSlideUp 0.4s ease-out 0.1s both',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-sm, 13px)',
    color: 'var(--secondary-text, rgba(255,255,255,0.6))',
    margin: 'var(--spacing-xs, 8px) 0 0 0',
    animation: 'idlePromptSlideUp 0.4s ease-out 0.2s both',
  };

  const actionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: 'var(--spacing-sm, 12px)',
    justifyContent: 'center',
    animation: 'idlePromptSlideUp 0.4s ease-out 0.3s both',
  };

  const stayButtonContent = actionState === 'loading' 
    ? <><Spinner /> Refreshing...</>
    : actionState === 'success'
      ? <><Checkmark /> Done!</>
      : 'Stay signed in';

  return (
    <>
      {/* Screen reader announcements */}
      {announcement && (
        <div 
          role="alert" 
          aria-live="assertive" 
          style={{ 
            position: 'absolute', 
            width: 1, 
            height: 1, 
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
          }}
        >
          {announcement}
        </div>
      )}
      
      <Modal 
        open={visible} 
        footer={null} 
        closable={false} 
        maskClosable={false} 
        centered 
        title={null}
        width={380}
      >
        <div style={containerStyle}>
          {/* Visual countdown ring */}
          <CountdownRing 
            remaining={remaining} 
            total={totalSeconds} 
            urgency={urgency} 
          />
          
          {/* Message banner with urgency styling */}
          <div style={bannerStyle} role="status" aria-live="polite">
            <p style={messageStyle}>
              {urgency === 'critical' 
                ? 'Session expiring!' 
                : urgency === 'warning'
                  ? 'Are you still there?'
                  : 'Session timeout'}
            </p>
            <p style={subtitleStyle}>
              {urgency === 'critical'
                ? 'You will be signed out in seconds'
                : 'You will be signed out due to inactivity'}
            </p>
          </div>

          {/* Action buttons */}
          <div style={actionsStyle}>
            <Button 
              type="primary" 
              size="large"
              onClick={handleStaySignedIn}
              disabled={actionState !== 'idle'}
              style={{
                minWidth: 160,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--spacing-xs, 8px)',
                transition: 'all 0.2s ease',
                transform: actionState === 'success' ? 'scale(1.02)' : undefined,
              }}
            >
              {stayButtonContent}
            </Button>
            <Button 
              size="large"
              onClick={handleSignOut}
              disabled={actionState === 'loading'}
              style={{
                opacity: actionState === 'success' ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              Sign out
            </Button>
          </div>

          {/* Keyboard hint */}
          <p 
            style={{ 
              textAlign: 'center', 
              fontSize: 'var(--font-size-xs, 11px)', 
              color: 'var(--tertiary-text, rgba(255,255,255,0.4))',
              margin: 0,
              animation: 'idlePromptSlideUp 0.4s ease-out 0.4s both',
            }}
          >
            Press <kbd style={{ 
              padding: '2px 6px', 
              borderRadius: 4, 
              background: 'var(--kbd-bg, rgba(255,255,255,0.1))',
              border: '1px solid var(--border-color, rgba(255,255,255,0.15))',
              fontFamily: 'inherit',
              fontSize: 'inherit',
            }}>Enter</kbd> to stay signed in
          </p>
        </div>
      </Modal>
    </>
  );
}


